import { createWebHistory, createRouter } from "vue-router";
import Swal from "sweetalert2";
import routes from "./routes";
import appConfig from "../../app.config";
import { withTwoFactorCode } from "@/services/twofactor-prompt";
import { verifyTwoFactorCode } from "@/services/auth.service";

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    return savedPosition || { top: 0, left: 0 };
  },
});

const PUBLIC_PATHS = ["/login", "/forgot-password"];

function userHasAccess(routeTo) {
  // A route can declare required permission(s) and/or role(s) via meta.permission
  // and meta.role (each can be a string or array). If declared, the user must
  // have at least one of each set.
  const need = {
    perms: [].concat(routeTo.meta.permission || []),
    roles: [].concat(routeTo.meta.role || []),
  };
  if (!need.perms.length && !need.roles.length) return true;
  const raw = localStorage.getItem("user");
  if (!raw) return false;
  let user;
  try { user = JSON.parse(raw); } catch (e) { return false; }
  const perms = user.permissions || [];
  const roles = user.roles || [];
  if (need.perms.length && !need.perms.some((p) => perms.includes(p))) return false;
  if (need.roles.length && !need.roles.some((r) => roles.includes(r))) return false;
  return true;
}

router.beforeEach((routeTo, routeFrom, next) => {
  const authRequired = routeTo.matched.some((route) => route.meta.authRequired);
  const isPublic = PUBLIC_PATHS.includes(routeTo.path);
  const loggedIn = !!localStorage.getItem("jwt");

  if (!authRequired || isPublic) return next();
  if (!loggedIn) {
    return next({ name: "login", query: { redirectFrom: routeTo.fullPath } });
  }
  if (!userHasAccess(routeTo)) {
    return next({ name: "error-403" });
  }
  return next();
});

// True when the target route's required permission is tagged "requires 2FA"
// for the current user (set by the API's formatUser → two_factor_permissions).
function routeNeedsTwoFactorCode(routeTo) {
  const need = [].concat(routeTo.meta.permission || []);
  if (!need.length) return false;
  const raw = localStorage.getItem("user");
  if (!raw) return false;
  let user;
  try { user = JSON.parse(raw); } catch (e) { return false; }
  const tagged = user.two_factor_permissions || [];
  return tagged.length > 0 && need.some((p) => tagged.includes(p));
}

// Step-up gate: entering a page whose required permission is tagged
// "requires 2FA" (managed in Admin → Permissions) prompts for a fresh
// authenticator code. The tag is the single switch — no per-page wiring.
// Runs after the auth/access guard above, so we know the user is allowed in.
router.beforeEach(async (routeTo, routeFrom) => {
  if (!routeNeedsTwoFactorCode(routeTo)) return true;
  // Don't re-prompt on param/query-only changes within the same page.
  if (routeTo.name && routeTo.name === routeFrom.name) return true;

  try {
    const res = await withTwoFactorCode(Swal, (code) => verifyTwoFactorCode(code));
    if (res && res.cancelled) {
      // Abort: stay where we were, or bail to Home on a cold/direct load.
      return routeFrom && routeFrom.name ? false : { name: "home" };
    }
    return true;
  } catch (e) {
    // Not enrolled yet / unexpected error — let the forced-enrollment modal
    // and the API guards handle it instead of trapping the user on the prompt.
    return true;
  }
});

router.beforeResolve(async (routeTo, routeFrom, next) => {
  try {
    for (const route of routeTo.matched) {
      await new Promise((resolve, reject) => {
        if (route.meta && route.meta.beforeResolve) {
          route.meta.beforeResolve(routeTo, routeFrom, (...args) => {
            if (args.length) {
              next(...args);
              reject(new Error("Redirected"));
            } else {
              resolve();
            }
          });
        } else {
          resolve();
        }
      });
    }
  } catch (e) {
    return;
  }
  document.title = (routeTo.meta.title || "") + " | " + appConfig.title;
  next();
});

export default router;
